import {type Accept, useDropzone} from 'react-dropzone';
import {Box, IconButton, Stack, Typography} from '@mui/material';
import {type Control, type FieldPath, type FieldValues, useController} from 'react-hook-form';
import {Close, CloudUpload} from '@mui/icons-material';
import type {MouseEvent} from 'react';

interface Props<T extends FieldValues> {
    control: Control<T>;
    name: FieldPath<T>;
    acceptFileTypes?: Accept;
}

export function FileInputForm<T extends FieldValues>({ control, name, acceptFileTypes }: Props<T>) {
    const { field, fieldState } = useController({
        control,
        name
    });

    const disabled = !!field.value;

    const onDrop = (acceptedFiles: File[]) => {
        if (!acceptedFiles.length) {
            return;
        }

        field.onChange(acceptedFiles[0]);
    };

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: acceptFileTypes
    });

    const handleRemove = (e: MouseEvent) => {
        e.stopPropagation();
        field.onChange(undefined);
    };

    return (
        <Box>
            <Box
                {...getRootProps()}
                sx={{
                    mt: 2,
                    p: 4,
                    cursor: disabled ? 'default' : 'pointer',
                    backgroundColor: 'common.background',
                    border: fieldState.error ? '1px solid red' : '1px dotted gray',
                    borderRadius: 2
                }}
            >
                <input {...getInputProps()} />

                {field.value ? (
                    <Stack
                        flexDirection="row"
                        gap={2}
                        alignItems="center"
                    >
                        <Typography>
                            {field.value.name}
                        </Typography>
                        <IconButton
                            onClick={handleRemove}
                            sx={{
                                color: 'error.main'
                            }}
                        >
                            <Close/>
                        </IconButton>
                    </Stack>
                ) : (
                    <>
                        <Stack
                            justifyContent="center"
                            alignItems="center"
                        >
                            <CloudUpload color="primary"/>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'primary.main',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    lineHeight: '28px'
                                }}
                            >
                                {isDragActive
                                    ? 'Перенесите файл сюда...'
                                    : 'Загрузить файл'}
                            </Typography>
                        </Stack>
                    </>
                )}
            </Box>

            {fieldState.error && (
                <Typography
                    variant="body2"
                    sx={{
                        color: 'error.main',
                        mt: 1
                    }}
                >
                    {fieldState.error.message}
                </Typography>
            )}
        </Box>

    );
}
